import { useMemo } from "react";
import { cn } from "../ui/cn";
import PersonAvatar from "./PersonAvatar";

// ---------------------------------------------------------------------------
// Mapa de calor de asistencia (CC-CONSOLE#1595): una fila por persona y una
// columna por día del rango. Cada celda es el estado de esa jornada; los
// retardos (entrada después de la tolerancia) y el tiempo extra se marcan
// encima. Es una vista: los datos son los mismos registros de la tabla.
// ---------------------------------------------------------------------------

export interface HeatmapRecord {
  id: string;
  date: string;
  teamMemberId: string;
  teamMemberName?: string | null;
  status?: string | null;
  checkIn?: string | null;
  overtime?: number | null;
  notes?: string | null;
  /** Hora de entrada esperada del turno (`HH:mm`); sin ella, 09:00. */
  shiftStart?: string | null;
}

/** Días que caben en el mapa; un rango más largo muestra los últimos. */
export const HEATMAP_MAX_DAYS = 62;
/** Tolerancia de entrada antes de contar un retardo. */
export const LATE_TOLERANCE_MIN = 10;
const DEFAULT_SHIFT_START = "09:00";

/** Color de la celda por estado (tokens: funcionan en claro y oscuro). */
export const HEATMAP_TONES: Record<string, string> = {
  present: "bg-success/80",
  late: "bg-warning/80",
  half_day: "bg-info/70",
  absent: "bg-error/80",
  sick_leave: "bg-violet-500/70",
  vacation: "bg-primary-500/60",
  leave: "bg-orange-500/70",
  holiday: "bg-subtle",
};

const toMinutes = (hhmm: string | null | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm ?? ""));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Llave de la celda: `late` si llegó tarde, si no su estado. Es retardo una
 * jornada presente cuya nota empieza con «Retardo» o cuya entrada pasó de la
 * hora esperada del turno (o de las 09:00) más la tolerancia de 10 minutos.
 */
export function heatmapKey(r: Pick<HeatmapRecord, "status" | "checkIn" | "notes" | "shiftStart">): string {
  const status = r.status || "present";
  if (status !== "present") return status;
  if (/^retardo/i.test(r.notes || "")) return "late";
  const checkIn = toMinutes(r.checkIn);
  const start = toMinutes(r.shiftStart) ?? (toMinutes(DEFAULT_SHIFT_START) as number);
  if (checkIn !== null && checkIn > start + LATE_TOLERANCE_MIN) return "late";
  return "present";
}

/** Gravedad para decidir la celda cuando hay dos registros el mismo día (mayor = peor). */
const SEVERITY: Record<string, number> = { present: 0, holiday: 1, vacation: 2, half_day: 3, late: 4, leave: 5, sick_leave: 6, absent: 7 };
const severityOf = (r: HeatmapRecord) => SEVERITY[heatmapKey(r)] ?? 0;

/** Días `YYYY-MM-DD` entre dos fechas (incluidas); tope de seguridad de un año. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const start = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return out;
  for (let t = start; t <= end && out.length < 366; t += 86400000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

const isWeekend = (date: string) => [0, 6].includes(new Date(`${date}T12:00:00Z`).getUTCDay());

interface Props {
  records: HeatmapRecord[];
  from: string;
  to: string;
  /** Etiqueta de cada estado (ya traducida). */
  labelOf: (key: string) => string;
  /** Color de avatar por persona, si se conoce. */
  colorOf?: (teamMemberId: string) => string | null | undefined;
  title: string;
  emptyLabel: string;
  overtimeLabel: string;
  /** Aviso cuando el rango no cabe (ya traducido), p. ej. «Mostrando los últimos 62 días». */
  truncatedLabel?: string;
  onSelect?: (record: HeatmapRecord) => void;
}

export default function AttendanceHeatmap({ records, from, to, labelOf, colorOf, title, emptyLabel, overtimeLabel, truncatedLabel, onSelect }: Props) {
  const { days, truncated } = useMemo(() => {
    // El rango pedido y, si algún registro cae fuera (o no hay rango cerrado),
    // también sus días: ninguna jornada se queda sin celda. Si no cabe, los últimos.
    const range = from && to ? daysBetween(from, to) : [];
    const all = [...new Set([...range, ...records.map((r) => r.date)])].sort();
    return { days: all.slice(-HEATMAP_MAX_DAYS), truncated: all.length > HEATMAP_MAX_DAYS };
  }, [records, from, to]);

  const people = useMemo(() => {
    const shown = new Set(days);
    const map = new Map<string, { id: string; name: string; byDate: Map<string, HeatmapRecord> }>();
    for (const r of records) {
      if (!shown.has(r.date)) continue;
      const p = map.get(r.teamMemberId) ?? { id: r.teamMemberId, name: r.teamMemberName || r.teamMemberId, byDate: new Map() };
      // Dos registros el mismo día: la celda muestra el peor estado.
      const prev = p.byDate.get(r.date);
      if (!prev || severityOf(r) > severityOf(prev)) p.byDate.set(r.date, r);
      map.set(r.teamMemberId, p);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [records, days]);

  // Totales y leyenda cuentan exactamente lo que se pinta (una celda por persona y día).
  const totals = useMemo(() => {
    const out: Record<string, number> = {};
    for (const p of people) {
      for (const r of p.byDate.values()) {
        const k = heatmapKey(r);
        out[k] = (out[k] ?? 0) + 1;
      }
    }
    return out;
  }, [people]);

  if (!people.length) {
    return (
      <div className="rounded-xl border border-line bg-card p-6 text-center text-sm text-content-secondary" data-testid="attendance-heatmap-empty">
        {emptyLabel}
      </div>
    );
  }

  const legend = Object.keys(HEATMAP_TONES).filter((k) => totals[k]);

  return (
    <section className="rounded-xl border border-line bg-card p-4" data-testid="attendance-heatmap">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-content">
          <i className="ri-calendar-2-line mr-1.5 text-primary-500" aria-hidden="true" />
          {title}
          {truncated && truncatedLabel && (
            <span className="ml-2 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning" data-testid="heatmap-truncated">
              {truncatedLabel}
            </span>
          )}
        </h3>
        <ul className="flex flex-wrap items-center gap-3 text-[11px] text-content-secondary">
          {legend.map((k) => (
            <li key={k} className="flex items-center gap-1.5" data-testid={`heatmap-legend-${k}`}>
              <span className={cn("h-3 w-3 rounded-sm", HEATMAP_TONES[k])} />
              {labelOf(k)} · {totals[k]}
            </li>
          ))}
          <li className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-content ring-1 ring-card" />
            {overtimeLabel}
          </li>
        </ul>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate" style={{ borderSpacing: "3px" }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-card" />
              {days.map((d) => (
                <th key={d} className={cn("min-w-[22px] text-center text-[10px] font-medium", isWeekend(d) ? "text-content-muted" : "text-content-secondary")} title={d}>
                  {Number(d.slice(8, 10))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.id} data-testid={`heatmap-row-${p.id}`}>
                <th scope="row" className="sticky left-0 z-10 bg-card pr-2 text-left">
                  <span className="flex items-center gap-2 whitespace-nowrap text-xs font-medium text-content">
                    <PersonAvatar name={p.name} color={colorOf?.(p.id)} size="xs" />
                    <span className="max-w-[10rem] truncate">{p.name}</span>
                  </span>
                </th>
                {days.map((d) => {
                  const r = p.byDate.get(d);
                  if (!r) {
                    return <td key={d} className={cn("h-6 rounded-sm", isWeekend(d) ? "bg-transparent" : "border border-dashed border-line")} aria-label={`${p.name} ${d}`} />;
                  }
                  const k = heatmapKey(r);
                  const tip = [`${p.name} · ${d}`, labelOf(k), r.checkIn ? `↦ ${r.checkIn}` : null, r.notes].filter(Boolean).join(" · ");
                  return (
                    <td key={d} className="p-0">
                      <button
                        type="button"
                        title={tip}
                        aria-label={tip}
                        data-testid={`heatmap-cell-${p.id}-${d}`}
                        data-status={k}
                        onClick={() => onSelect?.(r)}
                        className={cn("relative block h-6 w-full min-w-[22px] rounded-sm transition hover:ring-2 hover:ring-primary-500", HEATMAP_TONES[k] ?? "bg-subtle")}
                      >
                        {(Number(r.overtime) || 0) > 0 && <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-content ring-1 ring-card" data-testid="heatmap-overtime" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
